<template>
  <ContentView>
    <b-modal
      :active="isComponentModalActive"
      has-modal-card
      trap-focus
      aria-role="dialog"
      aria-label="FirstConnectionModal"
      :aria-modal="true"
      scroll="keep"
      class="FirstConnectionView"
    >
      <div
        v-if="pendingProjects && pendingProjects.length > 0 && currentStep === 0"
        class="modal-card"
      >
        <header class="modal-card-head">
          <H3TitleView class="modal-card-title"
            >{{ $t("myProjects.firstConnectionTitle").toString() }}
          </H3TitleView>
          <button
            type="button"
            class="delete"
            aria-label="close"
            @click="isComponentModalActive = false"
          ></button>
        </header>
        <section class="modal-card-body">
          <H4TitleView>{{ $t("myProjects.pendingInvit") }}</H4TitleView>
          <div class="FirstConnectionView-cards">
            <div v-if="isLoading">
              <BookmarkCard
                :is-loading="true"
                :tag-type="tagTypes.INVITATION"
                icon-name="paper-plane"
              />
            </div>
            <div v-for="project in pendingProjects" v-else :key="project.id">
              <PendingProjectCard
                :project="project"
                :logged-user-id="loggedUser ? loggedUser.id : ''"
                :send-to-server="false"
                @project-updated="updateProject"
              />
            </div>
          </div>
        </section>
        <footer class="modal-card-foot">
          <div class="buttons">
            <div
              v-if="projectsInEtablissement && projectsInEtablissement.length > 0"
              class="modal-finish-buttons"
            >
              <div class="hoverable-discrete-link" @click="endFirstConnection">
                {{ $t("annuler") }}
              </div>
              <b-button rounded type="is-primary" @click="sendAnswersAndGoNext">
                {{ $t("myProjects.nextStep") }}
              </b-button>
            </div>
            <div v-else class="modal-finish-buttons">
              <div class="hoverable-discrete-link" @click="endFirstConnection">
                {{ $t("annuler") }}
              </div>
              <b-button rounded type="is-primary" @click="sendAnswersAndClose">
                {{ $t("myProjects.end") }}
              </b-button>
            </div>
          </div>
        </footer>
      </div>

      <div
        v-if="
          projectsInEtablissement &&
          projectsInEtablissement.length > 0 &&
          (currentStep === 1 || !pendingProjects || pendingProjects.length === 0)
        "
        class="modal-card"
      >
        <header class="modal-card-head">
          <H3TitleView class="modal-card-title"
            >{{ $t("myProjects.firstConnectionTitle").toString() }}
          </H3TitleView>
        </header>
        <section class="modal-card-body">
          <H4TitleView>{{ $t("myProjects.joinProject") }}</H4TitleView>
          <div class="FirstConnectionView-cards">
            <div v-if="isLoading">
              <BookmarkCard :is-loading="true" :tag-type="tagTypes.JOIN" icon-name="pencil-ruler" />
            </div>
            <div v-for="project in projectsInEtablissement" v-else :key="project.id">
              <EtablissementProjectCard :project="project" :logged-user="loggedUser" />
            </div>
          </div>
        </section>
        <footer class="modal-card-foot">
          <div :class="`buttons modal-finish-buttons`">
            <div class="hoverable-discrete-link" @click="endFirstConnection">
              {{ $t("annuler") }}
            </div>
            <b-button rounded type="is-primary" @click="joinProjectsAndClose">
              {{ $t("myProjects.end") }}
            </b-button>
          </div>
        </footer>
      </div>
    </b-modal>
  </ContentView>
</template>

<script lang="ts">
import { defineComponent, PropType } from "vue"
import ContentView from "@/views/commons/ContentView.vue"
import H3TitleView from "../commons/H3TitleView.vue"
import H4TitleView from "../commons/H4TitleView.vue"
import { ProjetAnnuelDTO, UtilisateurDTO } from "@/model/bean/GeneratedDTOs"
import PendingProjectCard from "./PendingProjectCard.vue"
import EtablissementProjectCard from "./EtablissementProjectCard.vue"
import BookmarkCard from "../generic_components/BookmarkCard.vue"
import { TAG_TYPES_FOR_BOOKMARK_CARD } from "@/model/bean/EnumHelper"
import { BButton, BModal } from "buefy"

export default defineComponent({
  name: "FirstConnectionView",
  components: {
    H3TitleView,
    ContentView,
    H4TitleView,
    PendingProjectCard,
    EtablissementProjectCard,
    BookmarkCard,
    BButton,
    BModal,
  },
  props: {
    pendingProjects: {
      type: Array as PropType<ProjetAnnuelDTO[]>,
      required: true,
    },
    projectsInEtablissement: {
      type: Array as PropType<ProjetAnnuelDTO[]>,
      required: true,
    },
    isLoading: {
      type: Boolean,
      required: true,
    },
    loggedUser: {
      type: Object as PropType<UtilisateurDTO>,
      required: true,
    },
    isComponentModalActive: {
      type: Boolean,
      required: true,
    },
  },

  emits: ["pending-project-updated", "modal-closed", "send-answers", "projects-joined"],
  data() {
    return {
      currentStep: 0,
      tagTypes: TAG_TYPES_FOR_BOOKMARK_CARD,
    }
  },
  methods: {
    updateProject(updatedProject: ProjetAnnuelDTO): void {
      // Ne pas muter la prop, émettre un événement au parent
      this.$emit("pending-project-updated", updatedProject)
    },

    endFirstConnection(): void {
      this.$emit("modal-closed")
    },

    sendAnswersAndGoNext(): void {
      this.currentStep = 1
      this.$emit("send-answers", false)
    },

    sendAnswersAndClose(): void {
      this.$emit("send-answers", true)
    },

    joinProjectsAndClose(): void {
      this.$emit("projects-joined")
    },
  },
})
</script>

<style lang="scss">
@import "@/styles/variables";

.FirstConnectionView {
  .modal-card {
    height: $create-project-modals-height;
    overflow-y: auto !important;
  }
}

.FirstConnectionView-cards {
  display: flex;
  align-items: center;

  .BookmarkCard {
    margin-left: 0.5rem;
  }
}
</style>
